/**
 * SeasonalityModeView Helper Functions
 *
 * Pure utility functions extracted from SeasonalityModeView for better
 * maintainability and testability.
 */

import { MonthlyTotals, addMonthlyTotals } from '../../types';
import { HistoricalYearBundle } from './seasonalityModeTypes';

/**
 * Build a map of department -> total gross (including F&I for vehicle departments)
 * Used for seasonality weight calculations.
 */
export function buildTotalGrossMap(
  historicalData: HistoricalYearBundle | null
): Map<string, MonthlyTotals> {
  const map = new Map<string, MonthlyTotals>();
  if (!historicalData?.docGross) return map;

  // First, store all department gross values
  historicalData.docGross.forEach(d => {
    if (d.gross || d.monthlyGross) {
      map.set(d.department, d.gross || d.monthlyGross!);
    }
  });

  // For NEW_VEHICLE: combine with F_AND_I_NEW to get Total Gross
  const newVehicleGross = map.get('NEW_VEHICLE');
  const fiNewGross = map.get('F_AND_I_NEW');
  if (newVehicleGross) {
    map.set('NEW_VEHICLE', addMonthlyTotals(newVehicleGross, fiNewGross));
  }

  // For USED_VEHICLE: combine with F_AND_I_USED to get Total Gross
  const usedVehicleGross = map.get('USED_VEHICLE');
  const fiUsedGross = map.get('F_AND_I_USED');
  if (usedVehicleGross) {
    map.set('USED_VEHICLE', addMonthlyTotals(usedVehicleGross, fiUsedGross));
  }

  return map;
}

/**
 * Build a map of department -> total expenses
 * Used for expense seasonality calculations.
 */
export function buildExpenseMap(
  historicalData: HistoricalYearBundle | null
): Map<string, MonthlyTotals> {
  const map = new Map<string, MonthlyTotals>();
  if (!historicalData?.docGross) return map;

  historicalData.docGross.forEach(d => {
    if (d.totalExpenses) {
      map.set(d.department, d.totalExpenses);
    }
  });

  return map;
}

/**
 * Build a map of account code -> account data
 * Used for individual expense account seasonality.
 */
export function buildAccountMap(
  historicalData: HistoricalYearBundle | null
): Map<string, { ytdTotal: number; monthlyTotals: MonthlyTotals }> {
  const map = new Map<string, { ytdTotal: number; monthlyTotals: MonthlyTotals }>();
  if (!historicalData?.accounts) return map;

  historicalData.accounts.forEach(acc => {
    map.set(acc.account, { ytdTotal: acc.ytdTotal, monthlyTotals: acc.monthlyTotals });
  });

  return map;
}

/**
 * Calculate variance color class based on actual vs projected values
 * @param actual - Actual value
 * @param projected - Projected value
 * @param isExpense - If true, lower actual is better (green)
 * @returns Tailwind color class string
 */
export function getVarianceColor(
  actual: number,
  projected: number,
  isExpense: boolean
): string {
  if (actual === 0 && projected === 0) return '';
  const variance = actual - projected;
  if (isExpense) {
    // For expenses, lower is better (favorable)
    return variance < 0 ? 'text-green-600' : variance > 0 ? 'text-red-600' : '';
  } else {
    // For gross/profit, higher is better (favorable)
    return variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : '';
  }
}
